/**
 * LeaderboardLoadMore — tail control for the paginated squad leaderboard (Story 9).
 *
 * Renders at the bottom of the scroll-layout row list:
 *  - a spinner + "Loading…" while the next page is in flight, or
 *  - a "Show more" button while more pages remain (also the fallback affordance
 *    when scroll-end auto-load doesn't fire).
 * Renders nothing once the last page is loaded (`hasMore === false`).
 */

import React, { useMemo } from 'react';
import { View, Text, Pressable, ActivityIndicator, Platform } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { getLeaderboardLoadMoreStyles } from './LeaderboardLoadMore.styles';
import { LEADERBOARD_LOAD_MORE_COPY, LEADERBOARD_LOAD_MORE_TEST_ID } from './LeaderboardLoadMore.constants';

interface LeaderboardLoadMoreProps {
  hasMore: boolean;
  isLoading: boolean;
  onPress: () => void;
}

export const LeaderboardLoadMore: React.FC<LeaderboardLoadMoreProps> = ({
  hasMore,
  isLoading,
  onPress,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getLeaderboardLoadMoreStyles(theme), [theme]);

  if (isLoading) {
    return (
      <View style={styles.wrap} testID={`${LEADERBOARD_LOAD_MORE_TEST_ID}-loading`}>
        <View style={styles.spinnerRow}>
          <ActivityIndicator size="small" color={theme.gray[400]} />
          <Text style={styles.loadingLabel}>{LEADERBOARD_LOAD_MORE_COPY.LOADING}</Text>
        </View>
      </View>
    );
  }

  if (!hasMore) return null;

  return (
    <View style={styles.wrap}>
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={LEADERBOARD_LOAD_MORE_COPY.SHOW_MORE}
        testID={LEADERBOARD_LOAD_MORE_TEST_ID}
        style={({ pressed }) => [
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.buttonPressed,
        ]}
      >
        {({ hovered }) => (
          <View style={[styles.button, hovered && Platform.OS === 'web' && styles.buttonHover]}>
            <Text style={styles.label}>{LEADERBOARD_LOAD_MORE_COPY.SHOW_MORE}</Text>
          </View>
        )}
      </Pressable>
    </View>
  );
};
